import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import type { FieldErrors } from 'react-hook-form';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import dayjs from 'dayjs';

import { AppButton, AppCard, AppSpin } from '@/components/atoms';
import BottomBar from '@/components/organisms/Footer';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useRezeptInvoiceInit } from '@/hooks/main/useRezeptInvoiceInit';
import { useRezeptInvoiceMassagers } from '@/hooks/main/useRezeptInvoiceMassagers';
import { useRezeptInvoiceList } from '@/hooks/main/useRezeptInvoiceList';
import { useRezeptInvoiceListPdf } from '@/hooks/main/useRezeptInvoiceListPdf';
import { useRezeptFormatPdf } from '@/hooks/main/useRezeptFormatPdf';
import type { RezeptInvoiceListFilters } from '@/services/main/rezept';
import type { RezeptInvoiceSearchFormValues } from '@/schemas/rezeptInvoiceSearchSchema';
import {
  rezeptInvoicePrintOptionsSchema,
  DEFAULT_PRINT_OPTIONS,
  getDefaultPrintOptions,
  type RezeptInvoicePrintOptionsFormValues,
} from '@/schemas/rezeptInvoicePrintOptionsSchema';
import useAppModal from '@/hooks/useAppModal';
import { getApiErrorMessage } from '@/utils/apiError';
import RezeptInvoiceSearchFilter from './components/RezeptInvoiceSearchFilter';
import RezeptInvoiceListTable from './components/RezeptInvoiceListTable';
import RezeptInvoicePrintOptionsPanel from './components/RezeptInvoicePrintOptionsPanel';
import MonthlyRequestClinicModal from './components/MonthlyRequestClinicModal';
import {
  formValuesToSearchParams,
  searchParamsToFormValues,
  searchParamsToPage,
  searchParamsToPerPage,
  searchParamsToSort,
  type InvoiceSort,
} from './urlParams';

const DEFAULT_PER_PAGE = 20;

/**
 * 5xx and network failures already get the app-wide 「サーバーエラー」 dialog / connection toast
 * (shared/lib/react-query.ts), so the screen must not stack its own dialog on top of them.
 */
function isHandledGlobally(err: unknown): boolean {
  const e = err as { code?: string; message?: string; response?: { status?: number } };
  const status = e?.response?.status;
  if (typeof status === 'number' && status >= 500 && status < 600) return true;
  const offline = typeof window !== 'undefined' && !window.navigator.onLine;
  return offline || e?.code === 'ERR_NETWORK' || e?.message === 'Network Error';
}

function toApiFilters(
  values: RezeptInvoiceSearchFormValues,
  page: number,
  perPage: number,
  sort: InvoiceSort | null
): RezeptInvoiceListFilters {
  const massagerId = values.massagerId && values.massagerId > 0 ? values.massagerId : null;
  return {
    ...(sort ? { sort_by: sort.by, sort_dir: sort.dir } : {}),
    search_term_type: values.searchTermType,
    search_treatement_from: values.searchTreatementFrom ?? null,
    search_treatement_to: values.searchTreatementTo ?? null,
    search_treatement_one: values.searchTreatementOne ?? null,
    massager_id: massagerId,
    sort_radio: values.sortRadio,
    page,
    per_page: perPage,
  };
}

const RezeptInvoiceListPage: React.FC = () => {
  const { serviceType } = useTheme();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [formValues, setFormValues] = useState<RezeptInvoiceSearchFormValues | null>(null);
  const [page, setPage] = useState(() => searchParamsToPage(urlSearchParams));
  const [perPage, setPerPage] = useState(() =>
    searchParamsToPerPage(urlSearchParams, DEFAULT_PER_PAGE)
  );
  // HIT-14683: header-column sort is sent to the server so it orders ALL pages,
  // not only the rows of the page on screen.
  const [sort, setSort] = useState<InvoiceSort | null>(() => searchParamsToSort(urlSearchParams));
  const [tableResetKey, setTableResetKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const appModal = useAppModal();
  const sidebarOpen = useSidebarOpen();

  // Reset state when service type actually changes (A2 ↔ R2).
  // setUrlSearchParams is intentionally excluded from deps: its identity changes
  // on every URL update, which would re-fire this and wipe a fresh search.
  const lastServiceTypeRef = useRef(serviceType);
  useEffect(() => {
    if (lastServiceTypeRef.current === serviceType) return;
    lastServiceTypeRef.current = serviceType;
    setFormValues(null);
    setPage(1);
    setPerPage(DEFAULT_PER_PAGE);
    setSort(null);
    setSelectedIds([]);
    setUrlSearchParams(new URLSearchParams(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType]);

  // Init: fetch defaults for the search form on screen entry.
  const { data: initData, isLoading: isInitLoading } = useRezeptInvoiceInit();

  // Massager dropdown is keyed by treatement_yearmonth — derived from the
  // 施術開始期 (search_treatement_from) field. BD: reload when user picks a
  // new `from` date; do not reload on radio switch / `to` / 日別 changes.
  const [treatmentYearmonth, setTreatmentYearmonth] = useState<string>(() => {
    const fromUrl = urlSearchParams.get('search_treatement_from');
    if (fromUrl && /^\d{4}-\d{2}-\d{2}$/.test(fromUrl)) {
      const ym = dayjs(fromUrl).format('YYYYMM');
      if (/^\d{6}$/.test(ym)) return ym;
    }
    return dayjs().format('YYYYMM');
  });
  // After init resolves, sync from the BE-provided default in case it differs
  // from current month (e.g., session-restored date). URL value wins if present.
  useEffect(() => {
    if (urlSearchParams.has('search_treatement_from')) return;
    const from = initData?.data?.defaults?.search_treatement_from;
    if (!from) return;
    const ym = dayjs(from).format('YYYYMM');
    if (/^\d{6}$/.test(ym)) setTreatmentYearmonth(ym);
  }, [initData, urlSearchParams]);

  const { data: massagersData } = useRezeptInvoiceMassagers(treatmentYearmonth);

  const printOptionsForm = useTrimmedForm<RezeptInvoicePrintOptionsFormValues>({
    resolver: zodResolver(rezeptInvoicePrintOptionsSchema),
    defaultValues: DEFAULT_PRINT_OPTIONS,
    mode: 'onSubmit',
  });

  // UAT No147: print-time errors (印刷 / 雛形印刷) are shown in the same error dialog as
  // [来院用]領収書(窓口) (VisitClinicInvoicesPage runPrint → appModal.error({ title })), not a toast.
  const showPrintError = useCallback(
    (message: string) => {
      appModal.error({ title: message });
    },
    [appModal]
  );
  const showPrintApiError = useCallback(
    (err: unknown, fallback: string) => {
      if (isHandledGlobally(err)) return;
      showPrintError(getApiErrorMessage(err, fallback));
    },
    [showPrintError]
  );
  const showPrintValidationError = useCallback(
    (errors: FieldErrors<RezeptInvoicePrintOptionsFormValues>) => {
      const msg =
        errors.print_receipt?.message || errors.print_type?.message || errors.option_date?.message;
      if (msg) showPrintError(String(msg));
    },
    [showPrintError]
  );

  const pdfMutation = useRezeptInvoiceListPdf();
  const formatPdfMutation = useRezeptFormatPdf();

  const massagerOptions = useMemo(
    () =>
      (massagersData?.data?.massagers ?? []).map(m => ({
        value: m.id,
        label:
          m.serial_number != null && m.serial_number !== ''
            ? `${m.serial_number}:${m.name ?? ''}`
            : (m.name ?? `#${m.id}`),
      })),
    [massagersData]
  );

  // Form initial values: hydrate from URL if query present, else use BE defaults.
  // urlSearchParams is intentionally excluded from deps — initialValues are only
  // consumed by the search filter on first mount, and re-computing on every URL
  // change would just churn without effect.
  const initialFormValues = useMemo<Partial<RezeptInvoiceSearchFormValues> | undefined>(() => {
    const d = initData?.data?.defaults;
    const fromDefaults: Partial<RezeptInvoiceSearchFormValues> | undefined = d
      ? {
          searchTermType: d.search_term_type,
          searchTreatementFrom: d.search_treatement_from,
          searchTreatementTo: d.search_treatement_to,
          searchTreatementOne: dayjs().format('YYYY-MM-DD'),
        }
      : undefined;
    return searchParamsToFormValues(urlSearchParams, fromDefaults) ?? fromDefaults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData]);

  // On first mount, if URL already has filter params → kick off a search automatically.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!initData) return;
    hydratedRef.current = true;
    const fromUrl = searchParamsToFormValues(urlSearchParams, initialFormValues);
    if (!fromUrl) return;
    setFormValues(fromUrl);
    printOptionsForm.reset(getDefaultPrintOptions(fromUrl.searchTermType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData]);

  const filters = useMemo<RezeptInvoiceListFilters | undefined>(
    () => (formValues ? toApiFilters(formValues, page, perPage, sort) : undefined),
    [formValues, page, perPage, sort]
  );

  const { data, isLoading, isFetching } = useRezeptInvoiceList(filters, { enabled: !!filters });

  const items = data?.data?.data ?? [];
  const pagination = data?.data?.pagination ?? null;
  const allIds = data?.data?.all_ids ?? [];
  const orderedSelectedIds = useMemo(() => {
    if (selectedIds.length === 0) return [];
    const selectedSet = new Set(selectedIds);
    return allIds.filter(id => selectedSet.has(id));
  }, [allIds, selectedIds]);

  const handleSearch = (values: RezeptInvoiceSearchFormValues) => {
    setPage(1);
    setSort(null);
    setFormValues(values);
    setTableResetKey(key => key + 1);
    setSelectedIds([]);
    printOptionsForm.reset(getDefaultPrintOptions(values.searchTermType));
    setUrlSearchParams(formValuesToSearchParams(values, 1, perPage, DEFAULT_PER_PAGE), {
      replace: true,
    });
  };

  const handleChangePage = (nextPage: number, nextPageSize: number) => {
    setPage(nextPage);
    setPerPage(nextPageSize);
    if (formValues) {
      setUrlSearchParams(
        formValuesToSearchParams(formValues, nextPage, nextPageSize, DEFAULT_PER_PAGE, sort),
        { replace: true }
      );
    }
  };

  // A new sort re-orders the whole result, so go back to its first page.
  const handleChangeSort = (nextSort: InvoiceSort) => {
    setSort(nextSort);
    setPage(1);
    if (formValues) {
      setUrlSearchParams(
        formValuesToSearchParams(formValues, 1, perPage, DEFAULT_PER_PAGE, nextSort),
        { replace: true }
      );
    }
  };

  const runPrint = useCallback(
    (rezeptIds: number[], values: RezeptInvoicePrintOptionsFormValues) => {
      const pdfWindow = window.open('about:blank', '_blank');
      pdfMutation.mutate(
        {
          rezept_ids: rezeptIds,
          print_receipt: values.print_receipt,
          print_invoice: values.print_invoice,
          print_receipt_invoice: values.print_receipt_invoice,
          print_type: values.print_type as 1 | 2 | 3,
          search_term_type: (formValues?.searchTermType ?? 1) as 1 | 2,
          print_receipt_100_sub1: values.print_receipt_100_sub1,
          print_receipt_100_sub2: values.print_receipt_100_sub2,
          print_receipt_100_sub3: values.print_receipt_100_sub3,
          option_1: values.option_1,
          option_2: values.option_2,
          option_3: values.option_3,
          option_4: values.option_4,
          option_5: values.option_5,
          option_6: values.option_6,
          option_7: values.option_7,
          option_8: values.option_8,
          option_9: values.option_9,
          option_11: values.option_11,
          option_date: values.option_date,
          // 月別: pass from/to; 日別: searchTreatementOne mirrored into from/to by the
          // search filter, so passing from = to = selected day is enough.
          search_treatement_from:
            (formValues?.searchTermType === 2
              ? formValues?.searchTreatementOne
              : formValues?.searchTreatementFrom) ?? undefined,
          search_treatement_to:
            (formValues?.searchTermType === 2
              ? formValues?.searchTreatementOne
              : formValues?.searchTreatementTo) ?? undefined,
        },
        {
          onSuccess: url => {
            if (pdfWindow) {
              pdfWindow.location.href = url;
            } else {
              window.open(url, '_blank');
            }
          },
          onError: err => {
            if (pdfWindow) pdfWindow.close();
            showPrintApiError(err, '印刷に失敗しました。');
          },
        }
      );
    },
    [
      pdfMutation,
      showPrintApiError,
      formValues?.searchTermType,
      formValues?.searchTreatementFrom,
      formValues?.searchTreatementTo,
      formValues?.searchTreatementOne,
    ]
  );

  const handlePrint = useCallback(
    printOptionsForm.handleSubmit(
      values => {
        if (orderedSelectedIds.length === 0) {
          showPrintError('受診者を選択してください。');
          return;
        }
        runPrint(orderedSelectedIds, values);
      },
      showPrintValidationError
    ),
    [printOptionsForm, orderedSelectedIds, runPrint, showPrintError, showPrintValidationError]
  );

  // 令和8年法改正 No 3 — 月別明細書発行依頼書（別紙様式5）, button 「月別希望」 (HIT-13991).
  // Independent of the print options panel: opens a 施術所 popup whose default follows the
  // searched massager; no rezept, no validation, nothing marked 済.
  const [isMonthlyRequestOpen, setIsMonthlyRequestOpen] = useState(false);
  const monthlyRequestSearchParams = useMemo(() => {
    const isDaily = formValues?.searchTermType === 2;
    return {
      massager_id:
        formValues?.massagerId && formValues.massagerId > 0 ? formValues.massagerId : null,
      search_treatement_from:
        (isDaily ? formValues?.searchTreatementOne : formValues?.searchTreatementFrom) ?? null,
      search_treatement_to:
        (isDaily ? formValues?.searchTreatementOne : formValues?.searchTreatementTo) ?? null,
    };
  }, [formValues]);

  // フォーマット印刷 (BD): print an **empty** template (no rezept data filled).
  //   - No need to tick Section 3 rows (not related to a specific rezept).
  //   - Validate Print Settings: >=1 form (領収書/請求書/兼用) + print_type
  //     + option_date (if option_5 is ticked).
  //   - Do not update 済 and do not apply the 10割 override (because there is no data).
  //   - BE renders forms in the order 領収書 -> 請求書 -> 兼用 when multiple forms are ticked.
  const handleFormatPrint = useCallback(
    printOptionsForm.handleSubmit(
      values => {
        const pdfWindow = window.open('about:blank', '_blank');
        formatPdfMutation.mutate(
          {
            print_receipt: values.print_receipt,
            print_invoice: values.print_invoice,
            print_receipt_invoice: values.print_receipt_invoice,
            print_type: values.print_type as 1 | 2 | 3,
            search_term_type: (formValues?.searchTermType ?? 1) as 1 | 2,
            print_receipt_100_sub1: values.print_receipt_100_sub1,
            print_receipt_100_sub2: values.print_receipt_100_sub2,
            print_receipt_100_sub3: values.print_receipt_100_sub3,
            option_1: values.option_1,
            option_2: values.option_2,
            option_3: values.option_3,
            option_4: values.option_4,
            option_5: values.option_5,
            option_6: values.option_6,
            option_7: values.option_7,
            option_8: values.option_8,
            option_9: values.option_9,
            option_11: values.option_11,
            option_date: values.option_date,
            // 令和8年法改正 No 2 — the searched 施術期間 picks the blank form's layout
            // (pre-revision vs the new one with 訪問施術料5 / 明細書発行加算).
            search_treatement_from:
              (formValues?.searchTermType === 2
                ? formValues?.searchTreatementOne
                : formValues?.searchTreatementFrom) ?? undefined,
          },
          {
            onSuccess: url => {
              if (pdfWindow) pdfWindow.location.href = url;
              else window.open(url, '_blank');
            },
            onError: err => {
              if (pdfWindow) pdfWindow.close();
              showPrintApiError(err, 'フォーマット印刷に失敗しました。');
            },
          }
        );
      },
      showPrintValidationError
    ),
    [
      printOptionsForm,
      formatPdfMutation,
      showPrintApiError,
      showPrintValidationError,
      formValues?.searchTermType,
      formValues?.searchTreatementFrom,
      formValues?.searchTreatementOne,
    ]
  );

  if (isInitLoading || !initData) {
    return (
      <div className="flex items-center justify-center p-8">
        <AppSpin />
      </div>
    );
  }

  const showPanel = !!formValues && items.length > 0;

  return (
    <div className="space-y-4">
      <RezeptInvoiceSearchFilter
        initialValues={initialFormValues}
        massagerOptions={massagerOptions}
        onSearch={handleSearch}
        loading={isFetching}
        onTreatmentFromYearmonthChange={setTreatmentYearmonth}
      />

      {formValues && (
        <AppCard className="shadow-sm">
          <RezeptInvoiceListTable
            items={items}
            pagination={pagination}
            allIds={allIds}
            loading={isLoading || isFetching}
            resetKey={tableResetKey}
            selectedIds={selectedIds}
            onChangeSelectedIds={setSelectedIds}
            onChangePage={handleChangePage}
            sort={sort}
            onChangeSort={handleChangeSort}
            searchTermType={formValues.searchTermType}
            searchTreatmentOne={formValues.searchTreatementOne}
          />
        </AppCard>
      )}

      {showPanel && (
        <RezeptInvoicePrintOptionsPanel
          control={printOptionsForm.control}
          watch={printOptionsForm.watch}
          setValue={printOptionsForm.setValue}
          searchTermType={formValues?.searchTermType}
        />
      )}

      <BottomBar
        variant="action"
        visible={showPanel}
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
        left={
          <div className="flex gap-2">
            <AppButton
              type="primary"
              onClick={handlePrint}
              loading={pdfMutation.isPending}
              className="w-24 rounded"
            >
              印刷
            </AppButton>
            <AppButton
              type="primary"
              loading={formatPdfMutation.isPending}
              onClick={handleFormatPrint}
              className="rounded w-24 shrink-0 !border-white !bg-white !text-slate-600 hover:!bg-slate-100 hover:!text-slate-700 hover:!border-slate-100"
            >
              雛形印刷
            </AppButton>
            <AppButton
              type="primary"
              onClick={() => setIsMonthlyRequestOpen(true)}
              className="rounded w-24 shrink-0 !border-white !bg-white !text-slate-600 hover:!bg-slate-100 hover:!text-slate-700 hover:!border-slate-100"
            >
              月別希望
            </AppButton>
          </div>
        }
      />

      <MonthlyRequestClinicModal
        isOpen={isMonthlyRequestOpen}
        onClose={() => setIsMonthlyRequestOpen(false)}
        searchParams={monthlyRequestSearchParams}
      />
    </div>
  );
};

export default RezeptInvoiceListPage;
